import React, { createContext, useContext, useState } from 'react';
import type { ReactNode } from 'react';
import type { BrandProfile, ContentPiece } from '@/types';

// Context type for Arcitext app state
interface AppContextType {
  // Brand management
  selectedBrandId: string | null;
  setSelectedBrandId: (brandId: string | null) => void;
  brands: BrandProfile[];
  setBrands: (brands: BrandProfile[]) => void;

  // Content management
  selectedContentId: string | null;
  setSelectedContentId: (contentId: string | null) => void;
  contentPieces: ContentPiece[];
  setContentPieces: (content: ContentPiece[]) => void;

  // UI state
  isCreateBrandOpen: boolean;
  setIsCreateBrandOpen: (isOpen: boolean) => void;
  isCreateContentOpen: boolean;
  setIsCreateContentOpen: (isOpen: boolean) => void;
  isAnalyzing: boolean;
  setIsAnalyzing: (isAnalyzing: boolean) => void;
  isFocusMode: boolean;
  setIsFocusMode: (isFocusMode: boolean) => void;
}

// Create context
const AppContext = createContext<AppContextType | undefined>(undefined);

// Provider component
interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  // Brand management state
  const [selectedBrandId, setSelectedBrandId] = useState<string | null>(null);
  const [brands, setBrands] = useState<BrandProfile[]>([]);

  // Content management state
  const [selectedContentId, setSelectedContentId] = useState<string | null>(null);
  const [contentPieces, setContentPieces] = useState<ContentPiece[]>([]);

  // UI state
  const [isCreateBrandOpen, setIsCreateBrandOpen] = useState(false);
  const [isCreateContentOpen, setIsCreateContentOpen] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isFocusMode, setIsFocusMode] = useState(false);

  const contextValue: AppContextType = {
    // Brand management
    selectedBrandId,
    setSelectedBrandId,
    brands,
    setBrands,

    // Content management
    selectedContentId,
    setSelectedContentId,
    contentPieces,
    setContentPieces,

    // UI state
    isCreateBrandOpen,
    setIsCreateBrandOpen,
    isCreateContentOpen,
    setIsCreateContentOpen,
    isAnalyzing,
    setIsAnalyzing,
    isFocusMode,
    setIsFocusMode,
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

// Custom hook to use the context
export const useApp = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};
